import React, { ReactNode, useEffect, useState } from 'react';
import { Box, Stack } from '@mui/material';
import {
  MypageContentSx,
  ProfileAccordion,
  ProfileItem,
  SubMenuButton,
  SubMenuButtonProps,
} from '@/components/MenuDrawer/MyMenuDrawer/Main/style';
import Button from '@mui/material/Button';
import { MyInfo } from '@/types/api';
import { MyMenuContentWrapper } from '@/components/MenuDrawer/MyMenuDrawer/styles';
import { useChangePassport, usePassport } from '@/hooks/apiHooks';
import Loading from '@/components/MobileOrganization/Content/Loading';
import classNames from 'classnames';
import MyPageProfile, {
  defaultImageUrl,
} from '@/components/MenuDrawer/MyMenuDrawer/@components/Profile';
import { useTranslation } from 'react-i18next';
import CopyIcon from '@/public/icons/copy.svg';
import { useMypageEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { MypageEventName } from '@/constant/gtm';
import { MyMenuContentTitle } from '@/constant/mymenu';
import { useSnackbar } from '@/hooks/global-tools';
import { useReload } from '@/hooks/useReload';
import { clearAllPendingAiSummaries } from '@/utils/aiAutoFill';

export type MyPageMainProps = {
  info: {
    department: ReactNode;
    isJobDescriptionLoading: boolean;
    hasJobDescription: boolean;
    jobDescription: string;
    jobDescriptionSummary: string;
    jobDescriptionContent: string;
  } & Pick<
    MyInfo,
    'name' | 'nickname' | 'employeeNumber' | 'profileImageUrl' | 'companyName'
  >;
  onClickLogout: () => void;
  onClickEditProfile: () => void;

  menuData: MyMenuMenu[];
};

export type MyMenuMenu = {
  readonly title: string;
  readonly subMenu: SubMenuButtonProps[];
};

const MyPageMain = (props: MyPageMainProps) => {
  const {
    info: {
      name,
      nickname,
      employeeNumber,
      profileImageUrl = defaultImageUrl,
      companyName,
      department,
      hasJobDescription,
      isJobDescriptionLoading,
      jobDescriptionSummary,
      jobDescriptionContent,
      jobDescription,
    },
    menuData,
    onClickEditProfile,
    onClickLogout,
  } = props;
  const mypageEventTracker = useMypageEventTracker();

  const { data, isLoading } = usePassport();
  const { reload } = useReload();
  const [isRn, setIsRn] = useState(false);

  const onSelectProfile = async (passportId: string) => {
    reload(async () => {
      await useChangePassport(passportId);
      clearAllPendingAiSummaries();
    }, true);
  };

  const { showSuccess, showError } = useSnackbar();
  const { t } = useTranslation();

  const onEmployeeNumberClick = async () => {
    try {
      await navigator.clipboard.writeText(employeeNumber);
      showSuccess({
        message: t('사번이 복사되었습니다.'),
      });

      // GA Mypage - 4번
      mypageEventTracker(MypageEventName.clickEvent, {
        area_name: '마이페이지 목록',
        contents: employeeNumber,
        status: undefined,
        description: '사번 복사',
      });
    } catch (error) {
      showError({
        message: '사번 복사에 실패했어요',
      });
    }
  };

  useEffect(() => {
    //모바일 환경에서 Scroll bug로 rn일때만 overflow 상태를 변경한다.
    setIsRn(!!window?.isRNWebview);
  }, []);

  return (
    <MyMenuContentWrapper
      paddingBottom={'48px'}
      hasSidePadding={false}
      sx={{ overflow: isRn ? 'visible' : 'hidden auto' }}
    >
      <Box className={'mypage-content'} sx={MypageContentSx}>
        {/* 프로필 */}
        <MyPageProfile profileImageUrl={profileImageUrl}>
          <Stack
            direction={'column'}
            alignItems={'center'}
            spacing={'4px'}
            className={'profile'}
          >
            <p className={'ellipsis-line2'}>
              <span className={'name'}>{name}</span>
              {nickname && <span className={'nickname'}> • {nickname}</span>}
            </p>
            <p className={'companyName'}>{companyName}</p>
            <p className={'ellipsis-line2'}>{department}</p>
            {employeeNumber && (
              <p
                onClick={onEmployeeNumberClick}
                className={'employeeNumber'}
                style={{
                  borderBottom: '1px solid var(--dk-bg, #fff)',
                  cursor: 'pointer',
                }}
              >
                <span>{employeeNumber}</span>
                <CopyIcon
                  style={{ verticalAlign: 'middle', marginTop: '-3px' }}
                />
              </p>
            )}
          </Stack>
        </MyPageProfile>

        {/* 업무소개 */}
        <Box className={'jobWrap'}>
          {isJobDescriptionLoading && t('불러오는 중...')}
          {!isJobDescriptionLoading &&
            (hasJobDescription ? (
              <>
                <p className={'job'}>{jobDescription}</p>
                <p className={'summary ellipsis'}>{jobDescriptionSummary}</p>
                <p className={'content ellipsis-line2'}>
                  {jobDescriptionContent}
                </p>
              </>
            ) : (
              <p className={'empty'}>
                {t(
                  'MYPAGE.아래의 ‘프로필 편집’을 통해 업무 소개를 등록해주세요.',
                )}
                <br />
                {t('MYPAGE.등록된 소개는 내 임직원 카드에 노출됩니다.')}
              </p>
            ))}
        </Box>

        {/* 프로필 편집 */}
        <Button
          className={'edit-profile'}
          onClick={() => {
            onClickEditProfile();
            // GA Mypage - 5번
            mypageEventTracker(MypageEventName.clickEvent, {
              area_name: '마이페이지 목록',
              contents: '프로필 편집',
              status: undefined,
              description: '프로필 편집',
            });
          }}
        >
          {t('MYPAGE.프로필 편집')}
        </Button>

        {/* 프로필 전환 */}
        <ProfileAccordion
          defaultPassportId={data?.defaultPassport.passportId}
          profileList={data?.assignedPassports}
          sx={{ margin: '27px 0 0' }}
        >
          {isLoading || !data ? (
            <Box sx={{ textAlign: 'center' }}>
              <Loading />
            </Box>
          ) : (
            data.assignedPassports.map((item, index) => (
              <ProfileItem
                key={`profile-${index}`}
                profile={item}
                className={classNames({
                  selected: data.defaultPassport.passportId === item.passportId,
                })}
                onClick={() => {
                  onSelectProfile(item.passportId);

                  // GA Mypage - 6번
                  mypageEventTracker(MypageEventName.clickEvent, {
                    area_name: '마이페이지 목록',
                    contents: item.detail.companyName,
                    status: undefined,
                    description: '프로필 전환',
                  });
                }}
              />
            ))
          )}
        </ProfileAccordion>

        {menuData.map(({ title, subMenu }) => {
          return (
            <Stack
              key={title}
              component={'section'}
              direction={'column'}
              spacing={'12px'}
            >
              <h2>{t(`MYPAGE.${title}`)}</h2>
              {subMenu.map((sub) => {
                return (
                  <SubMenuButton
                    key={sub.menu}
                    {...sub}
                    onClick={(event) => {
                      sub.onClick?.(event);

                      // GA Mypage - 7번
                      mypageEventTracker(MypageEventName.clickEvent, {
                        area_name: '마이페이지 목록',
                        contents: MyMenuContentTitle[sub.menu],
                        status: undefined,
                        description: '메뉴 선택',
                      });
                    }}
                  />
                );
              })}
            </Stack>
          );
        })}

        <Button
          variant={'outlined'}
          size={'large'}
          onClick={() => {
            onClickLogout();
            // GA Mypage - 8번
            mypageEventTracker(MypageEventName.clickEvent, {
              area_name: '마이페이지 목록',
              contents: '로그아웃',
              status: undefined,
              description: '로그아웃',
            });
          }}
          sx={{ width: 'calc(100% - 40px)', margin: '28px 20px' }}
        >
          {t('로그아웃')}
        </Button>
      </Box>
    </MyMenuContentWrapper>
  );
};

export default MyPageMain;
